/**
 * Server-Sent Events (SSE) Client
 * For receiving real-time notifications from server
 */

export type NotificationMessage = {
  type: 'notification'
  data: {
    id: string
    type: string
    title: string
    content: string
    related_order_id?: string
    read: boolean
    created_at: string
  }
}

/** 角标刷新信号（方案A）：待办变化时后端广播，前端据此重拉 task-badges */
export type BadgesMessage = { type: 'badges' }

export type SSEMessage =
  | { type: 'connected'; userId: string }
  | { type: 'heartbeat' }
  | NotificationMessage
  | BadgesMessage

export class SSEClient {
  private eventSource: EventSource | null = null
  private reconnectTimer: NodeJS.Timeout | null = null
  private isManuallyDisconnected = false
  private reconnectAttempts = 0
  private maxReconnectAttempts = 10

  constructor(
    private onMessage: (message: SSEMessage) => void,
    private onError?: (error: Event) => void,
    private onConnect?: () => void
  ) {}

  connect() {
    if (this.eventSource) {
      return
    }

    this.isManuallyDisconnected = false

    const token = localStorage.getItem('access_token')
    if (!token) {
      // 静默处理：没有token时不连接SSE（可能是未登录）
      return
    }

    try {
      // Create EventSource with token as URL parameter
      const url = `/api/v1/notifications/stream?token=${encodeURIComponent(token)}`
      this.eventSource = new EventSource(url)
    } catch (error) {
      console.warn('Failed to create SSE connection:', error)
      return
    }

    this.eventSource.onopen = () => {
      // 连接成功，重置重连计数
      this.reconnectAttempts = 0
      this.onConnect?.()
      this.clearReconnectTimer()
    }

    this.eventSource.onmessage = (event) => {
      try {
        const message: SSEMessage = JSON.parse(event.data)
        this.onMessage(message)
      } catch (error) {
        console.error('Failed to parse SSE message:', error)
      }
    }

    this.eventSource.onerror = (event) => {
      // 只在非正常断开时记录错误（避免刷新页面时的噪音）
      if (this.eventSource && this.eventSource.readyState === EventSource.CLOSED) {
        console.warn('SSE connection closed, will retry...')
      }

      this.onError?.(event)

      // 清理当前连接
      if (this.eventSource) {
        this.eventSource.close()
        this.eventSource = null
      }

      // 如果不是手动断开，尝试重连
      if (!this.isManuallyDisconnected) {
        this.scheduleReconnect()
      }
    }
  }

  disconnect() {
    this.isManuallyDisconnected = true
    this.reconnectAttempts = 0
    this.clearReconnectTimer()

    if (this.eventSource) {
      this.eventSource.close()
      this.eventSource = null
    }
  }

  private scheduleReconnect() {
    this.clearReconnectTimer()

    // 检查是否超过最大重连次数
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.warn('SSE: 已达到最大重连次数，停止重连')
      return
    }

    this.reconnectAttempts++

    // 使用指数退避策略：1s, 2s, 4s, 8s... 最大30s
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts - 1), 30000)

    this.reconnectTimer = setTimeout(() => {
      if (this.eventSource) {
        this.eventSource.close()
        this.eventSource = null
      }
      this.connect()
    }, delay)
  }

  private clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }

  isConnected(): boolean {
    return this.eventSource !== null && this.eventSource.readyState === EventSource.OPEN
  }
}
